import {
  ApiError,
  iccidFailsChecksumOnly,
  identifierRejectionReason,
  isUnitTracked,
  type Product,
  queryKeys,
  type SkuResolution,
  type SkuSuggestion,
  STOCKLESS_PRODUCT_TYPES,
  UNIT_IDENTIFIER_LABELS,
  type UnitIdentifierKind,
  unitIdentifierKind,
} from '@mevaser/shared'
import {
  Alert,
  Badge,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Field,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
  useToast,
} from '@mevaser/ui'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useSearch } from '@tanstack/react-router'
import { Check, ScanLine, Trash2, Upload, X } from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { Page, PageHeader } from '../components/app-shell.js'
import { useDebouncedValue } from '../components/image-library-grid.js'
import { api } from '../lib/api.js'

interface ScannedRow {
  imei: string
  status: 'ok' | 'invalid' | 'bad-checksum' | 'duplicate-in-batch' | 'already-in-stock' | 'checking'
  /** Set when status is 'invalid' or 'bad-checksum': which specific check failed. */
  reason?: string
  /** Set once a 'bad-checksum' ICCID has been deliberately accepted (t0918) —
   *  the row is 'ok' from here on, but keeps a warning-tone badge so an
   *  overridden row stays visibly different from a clean scan. */
  badChecksum?: boolean
  /** Optional secondary ID (serial or ICCID), typed per row after scanning. */
  serialNumber?: string
}

/** A batch commits in a single request, so this is the practical ceiling on
 *  how many can go in before it — see receiveUnitsSchema (max 500). */
const MAX_BATCH_SIZE = 500

interface Location {
  id: string
  name: string
  type: string
  isActive: boolean
}

const INTAKE_TYPES = ['serialized', 'sim', 'digital_license']
const INTAKE_TYPE_LABEL: Record<string, string> = {
  serialized: 'Phone',
  sim: 'SIM',
  digital_license: 'License',
}
const INTAKE_TYPE_RANK: Record<string, number> = { serialized: 0, sim: 1, digital_license: 2 }
const PICKER_PAGE = 50

/** Phones ahead of SIMs ahead of licenses, A–Z within each. */
function byIntakeType(a: Product, b: Product): number {
  return a.type === b.type
    ? a.name.localeCompare(b.name)
    : (INTAKE_TYPE_RANK[a.type] ?? 9) - (INTAKE_TYPE_RANK[b.type] ?? 9)
}

/**
 * Search-as-you-type product chooser for intake. Matches name or SKU on the
 * server, so it reaches every product. Keyboard-first: arrows + Enter pick,
 * Escape clears. Once chosen it collapses to the product with a Change button.
 */
function ProductPicker({
  value,
  onChange,
  inputProps,
}: {
  value: Product | null
  onChange: (product: Product | null) => void
  inputProps: Record<string, unknown>
}) {
  const [search, setSearch] = useState('')
  const [active, setActive] = useState(0)
  const q = useDebouncedValue(search.trim(), 200)
  const { data, isFetching } = useQuery({
    queryKey: queryKeys.products.list({ q, forIntake: true }),
    queryFn: () =>
      api.get<{ items: Product[] }>('/products', {
        query: {
          q: q || undefined,
          type: INTAKE_TYPES.join(','),
          isActive: true,
          sort: 'name',
          order: 'asc',
          pageSize: PICKER_PAGE,
        },
      }),
    enabled: !value,
  })
  const results = useMemo(() => [...(data?.items ?? [])].sort(byIntakeType), [data])

  // biome-ignore lint/correctness/useExhaustiveDependencies: reset highlight whenever the query text changes
  useEffect(() => setActive(0), [q])

  if (value) {
    return (
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0">
          <p className="truncate text-sm font-medium">{value.name}</p>
          <p className="font-identifier text-xs text-foreground-muted">{value.sku}</p>
        </div>
        <Button variant="ghost" size="sm" onClick={() => onChange(null)}>
          Change
        </Button>
      </div>
    )
  }

  const pick = (product: Product) => {
    onChange(product)
    setSearch('')
  }

  return (
    <div className="space-y-2">
      <Input
        {...inputProps}
        type="search"
        autoFocus
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Search by name or SKU…"
        autoComplete="off"
        role="combobox"
        aria-expanded={results.length > 0}
        aria-controls="intake-product-list"
        aria-activedescendant={results[active] ? `intake-product-${results[active].id}` : undefined}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown') {
            e.preventDefault()
            setActive((i) => Math.min(i + 1, results.length - 1))
          } else if (e.key === 'ArrowUp') {
            e.preventDefault()
            setActive((i) => Math.max(i - 1, 0))
          } else if (e.key === 'Enter') {
            e.preventDefault()
            const chosen = results[active]
            if (chosen) pick(chosen)
          } else if (e.key === 'Escape') {
            setSearch('')
          }
        }}
      />
      {results.length > 0 ? (
        <ul
          id="intake-product-list"
          className="max-h-72 divide-y divide-border overflow-y-auto rounded-md border border-border"
        >
          {results.map((product, i) => (
            <li key={product.id} id={`intake-product-${product.id}`}>
              <button
                type="button"
                tabIndex={-1}
                className={`flex w-full items-center justify-between gap-2 px-2 py-2 text-start text-sm hover:bg-surface-sunken ${i === active ? 'bg-surface-sunken' : ''}`}
                onClick={() => pick(product)}
              >
                <span className="min-w-0">
                  <span className="block truncate font-medium">{product.name}</span>
                  <span className="block font-identifier text-2xs text-foreground-muted">
                    {product.sku}
                  </span>
                </span>
                <Badge>{INTAKE_TYPE_LABEL[product.type] ?? product.type}</Badge>
              </button>
            </li>
          ))}
          {results.length >= PICKER_PAGE ? (
            <li className="px-2 py-1.5 text-xs text-foreground-muted">
              Keep typing to narrow the list.
            </li>
          ) : null}
        </ul>
      ) : data && !isFetching ? (
        <p className="px-1 text-sm text-foreground-muted">No matching products.</p>
      ) : null}
    </div>
  )
}

/**
 * Serialized intake — the keyboard-first screen.
 *
 * A technician stands at a bench with a USB barcode scanner in one hand and a
 * box of phones in the other. The scanner types the IMEI and presses Enter, so
 * the input must never lose focus, the result must be visible without looking
 * away, and the whole batch must commit without touching a mouse.
 */
export function IntakePage() {
  const toast = useToast()
  const queryClient = useQueryClient()
  const inputRef = useRef<HTMLInputElement>(null)
  // Not schema-validated: this route only ever receives this from the
  // "Receive devices" action on a product row, which sets it itself.
  const search = useSearch({ strict: false }) as { productId?: string }

  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null)
  const productId = selectedProduct?.id ?? ''
  const [locationId, setLocationId] = useState<string>('')
  const [vendor, setVendor] = useState('')
  const [unitCost, setUnitCost] = useState('')
  const [purchaseOrderRef, setPurchaseOrderRef] = useState('')
  const [current, setCurrent] = useState('')
  const [rows, setRows] = useState<ScannedRow[]>([])
  const [importOpen, setImportOpen] = useState(false)

  // A "Receive devices" link on a product row lands here already knowing the
  // model — fetch that one product and pick it once.
  const prefillId = search.productId
  const prefilled = useRef(false)
  const { data: prefillProduct } = useQuery({
    queryKey: queryKeys.products.detail(prefillId ?? ''),
    queryFn: () => api.get<Product>(`/products/${prefillId}`),
    enabled: Boolean(prefillId) && !selectedProduct,
  })
  useEffect(() => {
    if (prefilled.current || !prefillProduct) return
    prefilled.current = true
    if (prefillProduct.isActive && INTAKE_TYPES.includes(prefillProduct.type)) {
      setSelectedProduct(prefillProduct)
    }
  }, [prefillProduct])

  // Defaults to the phone/IMEI shape until a product is actually chosen, same
  // as the placeholder below always showing something reasonable.
  const kind = unitIdentifierKind(selectedProduct?.type ?? 'serialized')
  const identifierLabel = UNIT_IDENTIFIER_LABELS[kind]
  const noun = kind === 'iccid' ? 'SIM' : kind === 'license' ? 'license' : 'device'

  // Suggestions only — the field stays free text, so a vendor not yet in the
  // directory can still be typed and recorded against the lot.
  const { data: vendorDirectory } = useQuery({
    queryKey: queryKeys.vendors.list({ isActive: true, pageSize: 200 }),
    queryFn: () =>
      api.get<{ items: { name: string }[] }>('/vendors', {
        query: { isActive: true, pageSize: 200 },
      }),
  })

  const { data: locations } = useQuery({
    queryKey: queryKeys.locations(),
    queryFn: () => api.get<Location[]>('/locations'),
  })
  const activeLocations = useMemo(() => (locations ?? []).filter((l) => l.isActive), [locations])

  useEffect(() => {
    if (!locationId && activeLocations.length) setLocationId(activeLocations[0]?.id ?? '')
  }, [activeLocations, locationId])

  const readyToScan = Boolean(productId && locationId)

  // Keep the scanner's keystrokes going into the field, whatever else happens.
  // biome-ignore lint/correctness/useExhaustiveDependencies: productId re-triggers focus on product change
  useEffect(() => {
    if (readyToScan) inputRef.current?.focus()
    // productId: choosing a different product while already ready also refocuses.
  }, [readyToScan, productId])

  const acceptedRows = useMemo(() => rows.filter((r) => r.status === 'ok'), [rows])

  // Classifies one or many IMEIs against the rows already on screen (and
  // against each other, for a pasted block). A single-scan call behaves
  // exactly as it always has; a bulk import goes through the same path so it
  // gets the same invalid/duplicate/already-in-stock handling for free.
  const addImeis = (raw: string[]) => {
    const imeis = raw.map((r) => r.trim()).filter(Boolean)
    if (imeis.length === 0) return

    const seen = new Set(rows.map((r) => r.imei))
    const newRows: ScannedRow[] = imeis.map((imei) => {
      const reason = identifierRejectionReason(kind, imei)
      let status: ScannedRow['status'] = 'ok'
      if (reason) {
        // A checksum-only ICCID failure is a warning the receiver can accept
        // (t0918) — a repeat scan of the same bad value stays hard, same as
        // any other duplicate.
        status =
          kind === 'iccid' && iccidFailsChecksumOnly(imei) && !seen.has(imei)
            ? 'bad-checksum'
            : 'invalid'
      } else if (seen.has(imei)) {
        status = 'duplicate-in-batch'
      }
      seen.add(imei)
      return reason ? { imei, status, reason } : { imei, status }
    })

    // Newest first: the rows just added are the ones the technician looks at.
    setRows((current) => [...newRows, ...current])
  }

  const addImei = (raw: string) => {
    addImeis([raw])
    setCurrent('')
  }

  // Deliberate, per-row: the receiver compared the screen to the printed
  // card and is accepting the ICCID exactly as it reads (t0918).
  const acceptBadChecksum = (index: number) =>
    setRows((current) =>
      current.map((row, i) =>
        i === index ? { ...row, status: 'ok' as const, badChecksum: true } : row,
      ),
    )

  const receive = useMutation({
    mutationFn: () =>
      api.post<{
        created: Array<{ id: string; imei: string }>
        rejected: Array<{ imei: string; reason: string }>
      }>('/units/receive', {
        productId,
        locationId,
        vendor: vendor || null,
        purchaseOrderRef: purchaseOrderRef || null,
        unitCostCents: unitCost ? Math.round(Number.parseFloat(unitCost) * 100) : null,
        units: acceptedRows.map((r) => {
          const serial = r.serialNumber?.trim()
          return {
            imei: r.imei,
            serialNumber: serial ? serial : null,
            acceptBadChecksum: r.badChecksum === true,
          }
        }),
        note: null,
      }),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.units.all() })

      if (result.rejected.length === 0) {
        toast.success(
          `${result.created.length} ${noun}${result.created.length === 1 ? '' : 's'} received`,
          kind === 'imei' ? 'They are now in the provisioning queue.' : 'They are ready to sell.',
        )
        setRows([])
      } else {
        // Keep the rejected rows on screen so they can be re-scanned; drop the
        // ones that made it in. Losing the whole list would mean re-scanning
        // everything to fix one bad read.
        const rejectedImeis = new Set(result.rejected.map((r) => r.imei))
        setRows((current) =>
          current
            .filter((r) => rejectedImeis.has(r.imei) || r.status !== 'ok')
            .map((r) =>
              rejectedImeis.has(r.imei) ? { ...r, status: 'already-in-stock' as const } : r,
            ),
        )
        toast.toast({
          tone: 'warning',
          title: `${result.created.length} received, ${result.rejected.length} could not be`,
          description: 'The rows still listed need attention.',
        })
      }
      inputRef.current?.focus()
    },
    onError: (error) => {
      toast.error(
        'Could not receive the batch',
        error instanceof ApiError ? error.message : undefined,
      )
    },
  })

  // ⌘/Ctrl+Enter commits, so the whole flow stays on the keyboard.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
        event.preventDefault()
        if (acceptedRows.length > 0 && !receive.isPending) receive.mutate()
      }
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [acceptedRows.length, receive])

  const statusBadge = (row: ScannedRow) => {
    switch (row.status) {
      case 'ok':
        return row.badChecksum ? (
          <Badge tone="warning">
            <Check className="size-3" aria-hidden="true" /> Ready — accepted as printed
          </Badge>
        ) : (
          <Badge tone="success">
            <Check className="size-3" aria-hidden="true" /> Ready
          </Badge>
        )
      case 'invalid':
        return <Badge tone="danger">{row.reason ?? `Not a valid ${identifierLabel}`}</Badge>
      case 'bad-checksum':
        return <Badge tone="warning">{row.reason ?? 'Check digit fails'}</Badge>
      case 'duplicate-in-batch':
        return <Badge tone="warning">Scanned twice</Badge>
      case 'already-in-stock':
        return <Badge tone="danger">Already in stock</Badge>
      default:
        return <Badge tone="neutral">Checking…</Badge>
    }
  }

  return (
    <Page>
      <PageHeader
        title="Receive devices"
        description="Scan each phone, SIM card or license number into a purchase lot. The batch commits together."
      />

      <div className="grid gap-5 lg:grid-cols-[320px_1fr]">
        <Card className="h-fit">
          <CardContent className="space-y-4 pt-4">
            <div data-product-picker>
              <Field label="Product" required>
                {(props) => (
                  <ProductPicker
                    inputProps={props}
                    value={selectedProduct}
                    onChange={setSelectedProduct}
                  />
                )}
              </Field>
            </div>

            <Field label="Location" required>
              {(props) => (
                <Select value={locationId} onValueChange={setLocationId}>
                  <SelectTrigger {...props}>
                    <SelectValue placeholder="Choose a location" />
                  </SelectTrigger>
                  <SelectContent>
                    {activeLocations.map((location) => (
                      <SelectItem key={location.id} value={location.id}>
                        {location.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </Field>

            <Field label="Vendor" hint="Optional — recorded against every unit in this lot">
              {(props) => (
                <Input
                  {...props}
                  value={vendor}
                  onChange={(e) => setVendor(e.target.value)}
                  placeholder="e.g. Acme Distribution"
                  list="vendor-suggestions"
                  autoComplete="off"
                />
              )}
            </Field>
            <datalist id="vendor-suggestions">
              {(vendorDirectory?.items ?? []).map((v) => (
                <option key={v.name} value={v.name} />
              ))}
            </datalist>

            <Field label="Unit cost" hint="Optional. What you paid per device.">
              {(props) => (
                <Input
                  {...props}
                  value={unitCost}
                  onChange={(e) => setUnitCost(e.target.value)}
                  inputMode="decimal"
                  placeholder="0.00"
                />
              )}
            </Field>

            <Field
              label="Invoice / PO reference"
              hint="Optional — recorded against every unit in this lot, for tracking whether the vendor's been paid."
            >
              {(props) => (
                <Input
                  {...props}
                  value={purchaseOrderRef}
                  onChange={(e) => setPurchaseOrderRef(e.target.value)}
                  placeholder="e.g. PO-1042"
                />
              )}
            </Field>
          </CardContent>
        </Card>

        <div className="space-y-4">
          <Card>
            <CardContent className="pt-4">
              {!readyToScan ? (
                <Alert tone="info">Choose a product and location to start scanning.</Alert>
              ) : null}

              <form
                onSubmit={(e) => {
                  e.preventDefault()
                  addImei(current)
                }}
              >
                <Field
                  label={`Scan or type an ${identifierLabel}`}
                  hint="A USB scanner types the number and presses Enter."
                >
                  {(props) => (
                    <div className="flex gap-2">
                      <Input
                        {...props}
                        ref={inputRef}
                        value={current}
                        onChange={(e) => setCurrent(e.target.value)}
                        disabled={!readyToScan}
                        identifier
                        className="h-10 text-md"
                        placeholder={
                          kind === 'license'
                            ? 'License number'
                            : kind === 'iccid'
                              ? '00000000000000000000'
                              : '000000000000000'
                        }
                        autoComplete="off"
                        // Focus must return here after every interaction, or the
                        // next scan is typed into nothing — except while the
                        // import dialog is open, or this steals focus from its
                        // textarea the instant it opens; or while a row's serial
                        // input has just taken focus, or this yanks it right back.
                        onBlur={(e) => {
                          const next = e.relatedTarget
                          if (
                            next instanceof HTMLElement &&
                            next.closest('[data-serial-input], [data-product-picker]')
                          ) {
                            return
                          }
                          if (readyToScan && !importOpen) {
                            setTimeout(() => inputRef.current?.focus(), 0)
                          }
                        }}
                      />
                      <Button type="submit" variant="secondary" size="lg" disabled={!readyToScan}>
                        Add
                      </Button>
                    </div>
                  )}
                </Field>
              </form>
            </CardContent>
          </Card>

          <Card>
            <div className="flex items-center justify-between border-b border-border px-4 py-2.5">
              <div className="flex items-center gap-2">
                <ScanLine className="size-4 text-foreground-muted" aria-hidden="true" />
                <p className="text-sm font-medium">
                  Scanned <span className="tabular">{rows.length}</span>
                </p>
                {rows.length > 0 ? (
                  <span className="text-xs text-foreground-muted tabular">
                    ({acceptedRows.length} ready)
                  </span>
                ) : null}
              </div>
              <div className="flex items-center gap-1">
                <Button variant="ghost" size="sm" onClick={() => setImportOpen(true)}>
                  <Upload /> Import order form
                </Button>
                {rows.length > 0 ? (
                  <Button variant="ghost" size="sm" onClick={() => setRows([])}>
                    <Trash2 /> Clear
                  </Button>
                ) : null}
              </div>
            </div>

            <div className="max-h-[45vh] overflow-y-auto scrollbar-thin">
              {rows.length === 0 ? (
                <p className="px-4 py-10 text-center text-sm text-foreground-muted">
                  Nothing scanned yet.
                </p>
              ) : (
                <ul>
                  {rows.map((row, index) => (
                    <li
                      key={`${row.imei}-${index}`}
                      className="flex items-center gap-3 border-b border-border px-4 py-2 last:border-0"
                    >
                      <span className="font-identifier text-sm">{row.imei}</span>
                      {row.status === 'ok' ? (
                        <Input
                          data-serial-input
                          identifier
                          value={row.serialNumber ?? ''}
                          onChange={(e) => {
                            const value = e.target.value
                            setRows((c) =>
                              c.map((r, i) => (i === index ? { ...r, serialNumber: value } : r)),
                            )
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault()
                              inputRef.current?.focus()
                            }
                          }}
                          maxLength={64}
                          placeholder={kind === 'iccid' ? 'Serial (optional)' : 'Serial / ICCID'}
                          aria-label={
                            kind === 'iccid'
                              ? `Serial for ${row.imei}`
                              : `Serial or ICCID for ${row.imei}`
                          }
                          className="h-7 w-44 text-xs"
                        />
                      ) : null}
                      <span className="ms-auto">{statusBadge(row)}</span>
                      {row.status === 'bad-checksum' ? (
                        <Button
                          type="button"
                          variant="secondary"
                          size="sm"
                          onClick={() => acceptBadChecksum(index)}
                        >
                          Accept as printed
                        </Button>
                      ) : null}
                      <button
                        type="button"
                        onClick={() => setRows((c) => c.filter((_, i) => i !== index))}
                        className="rounded p-1 text-foreground-subtle transition-colors hover:text-danger"
                        aria-label={`Remove ${row.imei}`}
                      >
                        <X className="size-3.5" aria-hidden="true" />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="flex items-center justify-between gap-3 border-t border-border px-4 py-3">
              <p className="text-xs text-foreground-muted">
                <kbd className="rounded border border-border px-1">⌘</kbd>
                <kbd className="ms-0.5 rounded border border-border px-1">↵</kbd> to commit
              </p>
              <Button
                variant="primary"
                size="lg"
                loading={receive.isPending}
                disabled={acceptedRows.length === 0}
                onClick={() => receive.mutate()}
              >
                Receive {acceptedRows.length > 0 ? acceptedRows.length : ''} {noun}
                {acceptedRows.length === 1 ? '' : 's'}
              </Button>
            </div>
          </Card>
        </div>
      </div>

      <ImportOrderFormDialog
        open={importOpen}
        onOpenChange={(open) => {
          setImportOpen(open)
          if (!open) inputRef.current?.focus()
        }}
        locationId={locationId}
      />
    </Page>
  )
}
interface ParsedImei {
  imei: string
  status: 'ok' | 'invalid' | 'duplicate'
}

/**
 * One order-form line, read without reference to the catalog: the SKU exactly
 * as the vendor typed it, plus whichever of an identifier (IMEI or ICCID) or a
 * quantity followed it. Both are kept because which one matters depends on
 * the product's type, and for a SKU that isn't in the catalog yet the type is
 * not known until these two decide it.
 */
interface ParsedLine {
  raw: string
  sku: string
  imei: string | null
  qty: number | null
}

function parseOrderFormLines(text: string): ParsedLine[] {
  const lines: ParsedLine[] = []
  for (const rawLine of text.split(/\r?\n/)) {
    const raw = rawLine.trim()
    if (!raw) continue
    const [sku, ...rest] = raw.split(/[\s,;\t]+/).filter(Boolean)
    if (!sku) continue
    // 10-22 covers both a loosely-scanned IMEI (15 digits) and an ICCID
    // (19-20 digits) — narrower than that and it risks matching a plain
    // quantity, wider and it's clearly neither.
    const imei =
      rest.map((t) => t.replace(/\D/g, '')).find((d) => d.length >= 10 && d.length <= 22) ?? null
    const qtyToken = rest.find((t) => /^-?\d+$/.test(t))
    const qty = qtyToken ? Number.parseInt(qtyToken, 10) : null
    lines.push({ raw, sku, imei, qty })
  }
  return lines
}

/** An identifier token found on a line naming a SKU the catalog doesn't have
 *  yet decides what kind of product to create it as: short enough to be an
 *  IMEI reads as a phone, long enough to be an ICCID reads as a SIM, and no
 *  identifier at all reads as counted stock. */
function inferDraftType(identifier: string | null): DraftGroup['type'] {
  if (!identifier) return 'stock'
  return identifier.length >= 18 ? 'sim' : 'serialized'
}

const DRAFT_TYPE_NOUN: Record<DraftGroup['type'], string> = {
  serialized: 'a phone (IMEI)',
  sim: 'a SIM card (ICCID)',
  stock: 'counted stock (quantity)',
}

/** SKUs differ only by case and separators, so lines are grouped by the
 *  normalized form — two spellings of one SKU must not become two drafts. */
function skuKey(sku: string): string {
  return sku.replace(/[^A-Za-z0-9]/g, '').toUpperCase()
}

/** A group of lines that all name one product the catalog already has. */
interface ParsedGroup {
  product: Product
  imeis: ParsedImei[] // unit-tracked products only (serialized or sim)
  qty: number // stock products only
}

/**
 * A group of lines naming a SKU the catalog does not have. The shape of the
 * lines decides the type (a phone-length identifier means a phone, an
 * ICCID-length one means a SIM, a bare count means counted stock), which is
 * the one field a product cannot be created without and the order form does
 * supply. Name and price it cannot supply, so they are collected from the
 * user before anything is created.
 */
interface DraftGroup {
  sku: string // as first typed, which is what gets created
  type: 'serialized' | 'stock' | 'sim'
  imeis: ParsedImei[]
  qty: number
  /** Catalog SKUs close enough to be a typo of this one, closest first. */
  suggestions: SkuSuggestion[]
  /** Set when the SKU cannot be created as typed, whatever the user names it. */
  invalidSku: string | null
}

interface ParseError {
  line: string
  reason: string
}

interface ParseResult {
  serialized: ParsedGroup[]
  sim: ParsedGroup[]
  stock: ParsedGroup[]
  drafts: DraftGroup[]
  errors: ParseError[]
}

/** Mirrors `skuSchema` on the server, so a SKU that cannot be created says so
 *  in the dialog instead of coming back as a 400 mid-import. */
function skuCreationProblem(sku: string): string | null {
  if (sku.length < 2) return 'SKU is too short to create'
  if (sku.length > 64) return 'SKU is too long to create'
  if (!/^[A-Za-z0-9._-]+$/.test(sku)) {
    return 'SKU can only hold letters, numbers, dot, dash and underscore'
  }
  return null
}

/**
 * A real shipment mixes phone models and accessories on one packing slip, so
 * each line names its own product by SKU rather than trusting a product
 * picked once for the whole paste. `SKU  IMEI` for a phone, `SKU  QTY` for a
 * counted product — whichever the SKU's own type calls for, decided by
 * looking the SKU up rather than guessing from the shape of what follows it.
 *
 * A SKU the catalog has never seen is not an error: it is a product waiting to
 * be created, either because the shipment really is the first of a new line or
 * because the SKU is a near miss on one that exists. Both outcomes stay on
 * screen for the user to settle — nothing here resolves a typo on its own.
 */
function planOrderForm(
  lines: ParsedLine[],
  resolutions: Map<string, SkuResolution>,
  accepted: Record<string, Product>,
): ParseResult {
  const serialized = new Map<string, ParsedGroup>()
  const sim = new Map<string, ParsedGroup>()
  const stock = new Map<string, ParsedGroup>()
  const drafts = new Map<string, DraftGroup>()
  const errors: ParseError[] = []
  const seenImeis = new Set<string>()

  for (const line of lines) {
    const key = skuKey(line.sku)
    const resolution = resolutions.get(key)
    // Still in flight: hold the line rather than calling it unknown.
    if (!resolution) continue
    const product = resolution.match ?? accepted[key] ?? null

    if (!product) {
      const type = inferDraftType(line.imei)
      const draft =
        drafts.get(key) ??
        ({
          sku: line.sku,
          type,
          imeis: [],
          qty: 0,
          suggestions: resolution.suggestions,
          invalidSku: skuCreationProblem(line.sku),
        } satisfies DraftGroup)

      // One new SKU cannot be created as more than one shape. The first
      // line's shape wins and the contradicting line is called out, because
      // silently filing an ICCID as a quantity of 19 digits is unrecoverable.
      if (draft.type !== type) {
        errors.push({
          line: line.raw,
          reason: `${draft.sku} is being created as ${DRAFT_TYPE_NOUN[draft.type]} — this line looks like ${DRAFT_TYPE_NOUN[type]}`,
        })
        drafts.set(key, draft)
        continue
      }

      if (type !== 'stock') {
        const imei = line.imei as string
        draft.imeis.push({
          imei,
          status: identifierStatus(imei, seenImeis, unitIdentifierKind(type)),
        })
        seenImeis.add(imei)
      } else if (line.qty !== null && line.qty > 0) {
        draft.qty += line.qty
      } else {
        errors.push({ line: line.raw, reason: 'Missing a quantity' })
      }
      drafts.set(key, draft)
      continue
    }

    if (STOCKLESS_PRODUCT_TYPES.includes(product.type)) {
      errors.push({ line: line.raw, reason: `${product.name} has no stock to receive` })
      continue
    }

    if (isUnitTracked(product.type)) {
      const kind = unitIdentifierKind(product.type)
      if (!line.imei) {
        errors.push({ line: line.raw, reason: `Missing an ${UNIT_IDENTIFIER_LABELS[kind]}` })
        continue
      }
      const byId = product.type === 'sim' ? sim : serialized
      const group = byId.get(product.id) ?? { product, imeis: [], qty: 0 }
      group.imeis.push({ imei: line.imei, status: identifierStatus(line.imei, seenImeis, kind) })
      seenImeis.add(line.imei)
      byId.set(product.id, group)
    } else {
      if (line.qty === null || !Number.isInteger(line.qty) || line.qty <= 0) {
        errors.push({ line: line.raw, reason: 'Missing a quantity' })
        continue
      }
      const group = stock.get(product.id) ?? { product, imeis: [], qty: 0 }
      group.qty += line.qty
      stock.set(product.id, group)
    }
  }

  return {
    serialized: [...serialized.values()],
    sim: [...sim.values()],
    stock: [...stock.values()],
    drafts: [...drafts.values()],
    errors,
  }
}

function identifierStatus(
  value: string,
  seen: Set<string>,
  kind: UnitIdentifierKind,
): ParsedImei['status'] {
  if (identifierRejectionReason(kind, value)) return 'invalid'
  return seen.has(value) ? 'duplicate' : 'ok'
}

interface ImportResult {
  productsCreated: number
  unitsCreated: number
  unitsRejected: number
  stockProducts: number
  failures: string[]
}

/** What the user has typed into one draft row. Price is held as the string
 *  they typed so a half-entered "12." does not fight the input. */
interface DraftEdit {
  name: string
  price: string
}

/** Waits for typing to settle before resolving SKUs, so pasting a hundred
 *  lines is one request rather than one per keystroke. */
function useDebounced<T>(value: T, ms: number): T {
  const [settled, setSettled] = useState(value)
  useEffect(() => {
    const timer = setTimeout(() => setSettled(value), ms)
    return () => clearTimeout(timer)
  }, [value, ms])
  return settled
}

/**
 * Bulk companion to the scan input, for a shipment that already exists as a
 * list — a full order form or packing slip, potentially with several phone
 * models and several accessories on it — rather than phones in hand.
 *
 * SKUs resolve server-side against the whole catalog rather than against a
 * page of it, because a client-side lookup over the first 200 active products
 * calls real SKUs unknown once the catalog outgrows that, and misses the
 * deactivated products whose SKUs are still taken.
 *
 * Products missing from the catalog are created here, inactive, so a shipment
 * is never blocked by a catalog gap and a nameless $0 product can never
 * reach the storefront or the register on its own.
 */
function ImportOrderFormDialog({
  open,
  onOpenChange,
  locationId,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  locationId: string
}) {
  const toast = useToast()
  const queryClient = useQueryClient()
  const [text, setText] = useState('')
  const [drafts, setDrafts] = useState<Record<string, DraftEdit>>({})
  const [accepted, setAccepted] = useState<Record<string, Product>>({})
  const fileInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (!open) {
      setText('')
      setDrafts({})
      setAccepted({})
    }
  }, [open])

  const lines = useMemo(() => parseOrderFormLines(text), [text])
  const skus = useMemo(() => {
    const unique = new Map<string, string>()
    for (const line of lines)
      if (!unique.has(skuKey(line.sku))) unique.set(skuKey(line.sku), line.sku)
    return [...unique.values()]
  }, [lines])

  // Debounced on a joined string so the query key is stable across renders
  // that reparse the same text.
  const settledSkus = useDebounced(skus.join('\n'), 350)
  const skuList = useMemo(() => (settledSkus ? settledSkus.split('\n') : []), [settledSkus])

  const { data: resolved, isFetching: resolving } = useQuery({
    queryKey: queryKeys.products.skuResolution(skuList),
    enabled: open && skuList.length > 0,
    queryFn: () =>
      api.post<{ results: SkuResolution[] }>('/products/resolve-skus', { skus: skuList }),
    staleTime: 30_000,
  })

  const resolutions = useMemo(() => {
    const map = new Map<string, SkuResolution>()
    for (const result of resolved?.results ?? []) map.set(skuKey(result.sku), result)
    return map
  }, [resolved])

  const parsed = useMemo(
    () => planOrderForm(lines, resolutions, accepted),
    [lines, resolutions, accepted],
  )

  const okImeiCount = [...parsed.serialized, ...parsed.sim].reduce(
    (sum, g) => sum + g.imeis.filter((i) => i.status === 'ok').length,
    0,
  )
  const totalToImport = okImeiCount + parsed.stock.length

  const draftEdit = (draft: DraftGroup): DraftEdit =>
    drafts[skuKey(draft.sku)] ?? { name: draft.sku, price: '' }
  const setDraftEdit = (draft: DraftGroup, patch: Partial<DraftEdit>) =>
    setDrafts((current) => {
      const key = skuKey(draft.sku)
      return {
        ...current,
        [key]: { ...(current[key] ?? { name: draft.sku, price: '' }), ...patch },
      }
    })

  // A draft is only creatable once it has a name and a SKU the server will
  // take, and only worth creating if it brings something to receive with it.
  const creatableDrafts = parsed.drafts.filter(
    (d) =>
      !d.invalidSku &&
      draftEdit(d).name.trim().length > 0 &&
      (d.type === 'serialized' ? d.imeis.some((i) => i.status === 'ok') : d.qty > 0),
  )
  const blockedDrafts = parsed.drafts.length - creatableDrafts.length
  const nothingToDo = totalToImport === 0 && creatableDrafts.length === 0

  const handleFile = async (file: File) => {
    const contents = await file.text()
    setText((current) => (current ? `${current}\n${contents}` : contents))
  }

  const commit = useMutation({
    mutationFn: async (): Promise<ImportResult> => {
      const result: ImportResult = {
        productsCreated: 0,
        unitsCreated: 0,
        unitsRejected: 0,
        stockProducts: 0,
        failures: [],
      }

      // Create first: a draft that cannot be created has nothing to receive
      // into, and one that fails must not take the rest of the slip with it.
      const created: Array<ParsedGroup & { type: DraftGroup['type'] }> = []
      for (const draft of creatableDrafts) {
        const edit = draftEdit(draft)
        const dollars = Number.parseFloat(edit.price)
        try {
          const product = await api.post<Product>('/products', {
            sku: draft.sku,
            type: draft.type,
            name: edit.name.trim(),
            priceCents: Number.isFinite(dollars) ? Math.round(dollars * 100) : 0,
            // Inactive on purpose: an unpriced product that could be sold is
            // worse than one the shipment has to be revisited to release.
            isActive: false,
          })
          result.productsCreated += 1
          created.push({ product, imeis: draft.imeis, qty: draft.qty, type: draft.type })
        } catch (err) {
          result.failures.push(
            `${draft.sku}: ${err instanceof ApiError ? err.message : 'could not be created'}`,
          )
        }
      }

      const unitTrackedGroups = [
        ...parsed.serialized,
        ...parsed.sim,
        ...created.filter((c) => c.type === 'serialized' || c.type === 'sim'),
      ]
      const stockGroups = [...parsed.stock, ...created.filter((c) => c.type === 'stock')]

      for (const group of unitTrackedGroups) {
        const okImeis = group.imeis.filter((i) => i.status === 'ok')
        if (okImeis.length === 0) continue
        try {
          const res = await api.post<{
            created: Array<{ id: string; imei: string }>
            rejected: Array<{ imei: string; reason: string }>
          }>('/units/receive', {
            productId: group.product.id,
            locationId,
            vendor: null,
            purchaseOrderRef: null,
            unitCostCents: null,
            units: okImeis
              .slice(0, MAX_BATCH_SIZE)
              .map((i) => ({ imei: i.imei, serialNumber: null })),
            note: 'Order form import',
          })
          result.unitsCreated += res.created.length
          result.unitsRejected += res.rejected.length
        } catch (err) {
          result.failures.push(
            `${group.product.name}: ${err instanceof ApiError ? err.message : 'could not be received'}`,
          )
        }
      }

      for (const group of stockGroups) {
        try {
          await api.post('/stock/adjust', {
            productId: group.product.id,
            locationId,
            qtyDelta: group.qty,
            reason: 'receive',
            note: 'Order form import',
          })
          result.stockProducts += 1
        } catch (err) {
          result.failures.push(
            `${group.product.name}: ${err instanceof ApiError ? err.message : 'could not be received'}`,
          )
        }
      }

      return result
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.units.all() })
      queryClient.invalidateQueries({ queryKey: queryKeys.stock.all() })
      if (result.productsCreated > 0) {
        queryClient.invalidateQueries({ queryKey: queryKeys.products.all() })
      }

      const parts: string[] = []
      if (result.productsCreated > 0) {
        parts.push(
          `${result.productsCreated} product${result.productsCreated === 1 ? '' : 's'} created`,
        )
      }
      if (result.unitsCreated > 0) {
        parts.push(`${result.unitsCreated} unit${result.unitsCreated === 1 ? '' : 's'} received`)
      }
      if (result.stockProducts > 0) {
        parts.push(
          `${result.stockProducts} counted product${result.stockProducts === 1 ? '' : 's'} restocked`,
        )
      }
      const problems = result.unitsRejected + result.failures.length

      if (problems === 0) {
        toast.success(parts.join(', ') || 'Import complete')
        if (result.productsCreated > 0) {
          toast.toast({
            tone: 'warning',
            title: `${result.productsCreated} new product${result.productsCreated === 1 ? '' : 's'} need pricing`,
            description: 'They were created inactive, so they will not sell until you price them.',
          })
        }
        setText('')
        setDrafts({})
        setAccepted({})
        onOpenChange(false)
      } else {
        toast.toast({
          tone: 'warning',
          title: parts.join(', ') || 'Partially imported',
          description: `${problems} product${problems === 1 ? '' : 's'} need attention — see below.`,
        })
      }
    },
    onError: (error) =>
      toast.error('Could not import', error instanceof ApiError ? error.message : undefined),
  })

  const importLabel = () => {
    const parts: string[] = []
    if (creatableDrafts.length > 0) parts.push(`create ${creatableDrafts.length}`)
    if (totalToImport > 0) parts.push(`import ${totalToImport}`)
    if (parts.length === 0) return 'Import products'
    const label = parts.join(' + ')
    return `${label.charAt(0).toUpperCase()}${label.slice(1)} product${
      creatableDrafts.length + totalToImport === 1 ? '' : 's'
    }`
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Import order form</DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-3">
          <Field
            label="Paste the order form, or choose a file"
            hint="One line per item: SKU then an IMEI for a phone, an ICCID for a SIM, or a quantity for a counted product. SKUs the catalog doesn't have yet can be created below."
          >
            {(props) => (
              <Textarea
                {...props}
                rows={8}
                className="font-identifier"
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder={'PHN-A36  490154203237518\nACC-CASE-01  25\n…'}
              />
            )}
          </Field>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,.txt,text/plain,text/csv"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0]
              if (file) void handleFile(file)
              e.target.value = ''
            }}
          />
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={() => fileInputRef.current?.click()}
          >
            <Upload /> Choose file…
          </Button>

          {parsed.serialized.length > 0 || parsed.sim.length > 0 || parsed.stock.length > 0 ? (
            <ul className="space-y-1 text-xs">
              {[...parsed.serialized, ...parsed.sim].map((group) => {
                const ok = group.imeis.filter((i) => i.status === 'ok').length
                const invalid = group.imeis.filter((i) => i.status === 'invalid').length
                const dup = group.imeis.filter((i) => i.status === 'duplicate').length
                return (
                  <li key={group.product.id} className="flex items-center gap-2">
                    <Badge tone="success">{ok} ready</Badge>
                    <span className="font-medium">{group.product.name}</span>
                    <span className="text-foreground-muted">({group.product.sku})</span>
                    {invalid > 0 ? <span className="text-danger">{invalid} invalid</span> : null}
                    {dup > 0 ? <span className="text-warning">{dup} duplicate</span> : null}
                  </li>
                )
              })}
              {parsed.stock.map((group) => (
                <li key={group.product.id} className="flex items-center gap-2">
                  <Badge tone="success">qty {group.qty}</Badge>
                  <span className="font-medium">{group.product.name}</span>
                  <span className="text-foreground-muted">({group.product.sku})</span>
                </li>
              ))}
            </ul>
          ) : null}

          {resolving && parsed.drafts.length === 0 && skus.length > 0 ? (
            <p className="text-foreground-muted text-xs">Looking up {skus.length} SKUs…</p>
          ) : null}

          {parsed.drafts.length > 0 ? (
            <div className="space-y-2">
              <p className="font-medium text-sm">
                {parsed.drafts.length} SKU{parsed.drafts.length === 1 ? '' : 's'} not in the catalog
                — name {parsed.drafts.length === 1 ? 'it' : 'them'} to create{' '}
                {parsed.drafts.length === 1 ? 'it' : 'them'}
              </p>
              {parsed.drafts.map((draft) => {
                const edit = draftEdit(draft)
                const ok = draft.imeis.filter((i) => i.status === 'ok').length
                const bad = draft.imeis.length - ok
                return (
                  <div
                    key={skuKey(draft.sku)}
                    className="space-y-2 rounded-md border border-border bg-surface-sunken p-3"
                  >
                    <div className="flex flex-wrap items-center gap-2 text-xs">
                      <span className="font-identifier font-medium">{draft.sku}</span>
                      <Badge tone="neutral">
                        {draft.type === 'serialized'
                          ? `phone · ${ok} IMEI`
                          : draft.type === 'sim'
                            ? `SIM · ${ok} ICCID`
                            : 'counted stock'}
                      </Badge>
                      {draft.type === 'stock' ? (
                        <span className="text-foreground-muted">qty {draft.qty}</span>
                      ) : null}
                      {bad > 0 ? (
                        <span className="text-warning">
                          {bad} {draft.type === 'sim' ? 'ICCID' : 'IMEI'} not usable
                        </span>
                      ) : null}
                    </div>

                    {draft.invalidSku ? (
                      <p className="text-danger text-xs">{draft.invalidSku}</p>
                    ) : (
                      <div className="flex flex-wrap items-end gap-2">
                        <Field label="Name" className="min-w-52 flex-1">
                          {(props) => (
                            <Input
                              {...props}
                              value={edit.name}
                              placeholder={draft.sku}
                              onChange={(e) => setDraftEdit(draft, { name: e.target.value })}
                            />
                          )}
                        </Field>
                        <Field label="Price" className="w-28">
                          {(props) => (
                            <Input
                              {...props}
                              inputMode="decimal"
                              value={edit.price}
                              placeholder="0.00"
                              onChange={(e) => setDraftEdit(draft, { price: e.target.value })}
                            />
                          )}
                        </Field>
                      </div>
                    )}

                    {draft.suggestions.length > 0 ? (
                      <div className="flex flex-wrap items-center gap-2 text-xs">
                        <span className="text-foreground-muted">
                          {draft.suggestions.length === 1 ? 'Did you mean' : 'Close matches:'}
                        </span>
                        {draft.suggestions.map((s) => (
                          <Button
                            key={s.product.id}
                            type="button"
                            variant="secondary"
                            size="sm"
                            onClick={() =>
                              setAccepted((current) => ({
                                ...current,
                                [skuKey(draft.sku)]: s.product,
                              }))
                            }
                          >
                            {s.product.sku} · {s.product.name}
                          </Button>
                        ))}
                      </div>
                    ) : null}
                  </div>
                )
              })}
              {Object.keys(accepted).length > 0 ? (
                <Button type="button" variant="ghost" size="sm" onClick={() => setAccepted({})}>
                  <X /> Undo {Object.keys(accepted).length} SKU correction
                  {Object.keys(accepted).length === 1 ? '' : 's'}
                </Button>
              ) : null}
              <p className="text-foreground-muted text-xs">
                New products are created inactive — price them in Products before they can sell.
              </p>
            </div>
          ) : null}

          {parsed.errors.length > 0 ? (
            <Alert tone="warning">
              <p className="mb-1 font-medium">
                {parsed.errors.length} line{parsed.errors.length === 1 ? '' : 's'} could not be
                matched
              </p>
              <ul className="space-y-0.5 font-identifier text-xs">
                {parsed.errors.slice(0, 15).map((e, i) => (
                  <li key={`${e.line}-${i}`}>
                    {e.line} — {e.reason}
                  </li>
                ))}
              </ul>
              {parsed.errors.length > 15 ? (
                <p className="mt-1 text-xs">and {parsed.errors.length - 15} more.</p>
              ) : null}
            </Alert>
          ) : null}

          {blockedDrafts > 0 ? (
            <Alert tone="warning">
              {blockedDrafts} new SKU{blockedDrafts === 1 ? '' : 's'} still{' '}
              {blockedDrafts === 1 ? 'needs' : 'need'} a name, a quantity or a usable IMEI before{' '}
              {blockedDrafts === 1 ? 'it' : 'they'} can be created.
            </Alert>
          ) : null}

          {!locationId ? <Alert tone="danger">Choose a location before importing.</Alert> : null}
        </DialogBody>
        <DialogFooter>
          <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            type="button"
            variant="primary"
            loading={commit.isPending}
            disabled={nothingToDo || !locationId || resolving}
            onClick={() => commit.mutate()}
          >
            {importLabel()}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
